import { useRef, useState } from 'react';
import { ImagePlus, Link2, Loader2, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { uploadPeriodicoImage } from '@/lib/actions';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

const MAX_MB = 8;

/**
 * Campo de imagen de un artículo del periódico. Permite subir un archivo
 * directamente a Storage o pegar un enlace. Renderiza un input oculto
 * `name="imageUrl"` → compatible con el envío por FormData de los formularios
 * existentes.
 */
export function ArticleImageField({ defaultValue, name = 'imageUrl' }: { defaultValue?: string; name?: string }) {
  const { toast } = useToast();
  const [url, setUrl] = useState(defaultValue || '');
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState<'upload' | 'link'>('link');
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file?: File | null) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Archivo no válido', description: 'Selecciona una imagen.', variant: 'destructive' });
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast({ title: 'Imagen demasiado grande', description: `El máximo son ${MAX_MB} MB.`, variant: 'destructive' });
      return;
    }
    setUploading(true);
    const res = await uploadPeriodicoImage(file);
    setUploading(false);
    if (res.success && res.url) {
      setUrl(res.url);
      toast({ title: 'Imagen subida', description: 'Ya está lista para publicarse.' });
    } else {
      toast({ title: 'Error', description: res.message || 'No se pudo subir la imagen.', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-2">
      <Label>Imagen del artículo</Label>

      {/* Valor real que viaja en el FormData */}
      <input type="hidden" name={name} value={url} />

      {url ? (
        <div className="relative overflow-hidden rounded-xl border border-white/10">
          <img src={url} alt="Portada del artículo" className="h-48 w-full object-cover" />
          <Button
            type="button" size="icon" variant="destructive"
            className="absolute right-2 top-2 h-8 w-8"
            onClick={() => setUrl('')}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <>
          <div className="inline-flex rounded-xl border border-white/10 bg-card/40 p-1">
            {([['upload', 'Subir archivo', <Upload className="h-3.5 w-3.5" key="u" />],
               ['link', 'Pegar enlace', <Link2 className="h-3.5 w-3.5" key="l" />]] as const).map(([key, lbl, ic]) => (
              <button
                key={key}
                type="button"
                onClick={() => setMode(key as 'upload' | 'link')}
                className={cn(
                  'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors',
                  mode === key ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {ic} {lbl}
              </button>
            ))}
          </div>

          {mode === 'upload' ? (
            <div
              onClick={() => !uploading && fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
              className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-white/12 bg-card/20 py-10 text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-7 w-7 animate-spin text-primary" />
                  <p className="text-sm">Subiendo imagen…</p>
                </>
              ) : (
                <>
                  <ImagePlus className="h-7 w-7" />
                  <p className="text-sm font-medium">Haz clic o arrastra una imagen aquí</p>
                  <p className="text-xs text-muted-foreground/70">JPG, PNG o WEBP · máx. {MAX_MB} MB</p>
                </>
              )}
              <input
                ref={fileRef} type="file" accept="image/*" className="hidden"
                onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }}
              />
            </div>
          ) : (
            <Input
              placeholder="https://i.imgur.com/..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
          )}
        </>
      )}
    </div>
  );
}

export default ArticleImageField;
